import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import PreferencesService from '../services/preferencesService';

const usePreferencesStore = create()(
  devtools(
    persist(
      (set, get) => ({
        // Preferences state
        preferences: {
          dayViewStartTime: '08:00',
          dayViewEndTime: '18:00',
          timezone: 'America/Mexico_City',
          calendarDotColor: '#3182ce'
        },
        isLoading: false,
        error: null,
        lastFetch: null,

        // Actions
        fetchPreferences: async (token) => {
          const state = get();

          // Avoid duplicate requests
          if (state.isLoading) return;

          // Check if we recently fetched (within 5 minutes)
          const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
          if (state.lastFetch && state.lastFetch > fiveMinutesAgo) {
            return state.preferences;
          }

          set({ isLoading: true, error: null });

          try {
            const data = await PreferencesService.getUserPreferences(token);

            set({
              preferences: {
                ...data
              },
              isLoading: false,
              error: null,
              lastFetch: Date.now()
            });

            return get().preferences;
          } catch (error) {
            console.error('Error fetching preferences:', error);

            set({
              error: error.message || 'Failed to load preferences',
              isLoading: false
            });

            // Return current preferences on error
            return state.preferences;
          }
        },

        updatePreferences: async (newPreferences, token) => {
          try {
            const success = await PreferencesService.saveUserPreferences(newPreferences, token);

            if (success) {
              set((state) => ({
                preferences: { ...state.preferences, ...newPreferences },
                error: null,
                lastFetch: null // Invalidate cache to force re-fetch next time
              }));

              // Immediately refresh to get updated preferences
              get().fetchPreferences(token);
            }

            return success;
          } catch (error) {
            console.error('Error updating preferences:', error);

            set({
              error: error.message || 'Failed to update preferences'
            });

            return false;
          }
        },

        // Reset preferences to defaults
        resetPreferences: () => {
          set({
            preferences: {
              dayViewStartTime: '08:00',
              dayViewEndTime: '18:00',
              timezone: 'America/Mexico_City',
              treatmentPlanValidityDays: 30,
              calendarDotColor: '#3182ce'
            },
            error: null
          });
        },

        // Clear error
        clearError: () => {
          set({ error: null });
        },

        // Force refresh preferences
        refreshPreferences: async (token) => {
          set({ lastFetch: null });
          return get().fetchPreferences(token);
        }
      }),
      {
        name: 'dental-preferences',
        partialize: (state) => ({
          // Only persist the preferences, not loading states
          preferences: state.preferences
        })
      }
    ),
    {
      name: 'preferences-store'
    }
  )
);

export default usePreferencesStore;
